import {
	Checkbox,
	createStyles,
	Divider,
	Group,
	Stack,
	useMantineTheme,
} from '@mantine/core';
import { EntityType } from '@repo/common/enums/entityType';
import { getEntityTypeDisplayInfo } from '@repo/common/utils/entityDisplayUtils';
import { Button, Select, Text, TextInput } from '@repo/foundations';
import { isEmpty } from 'lodash-es';
import { memo, useCallback, useMemo } from 'react';
import { CATALOG_ENTITY_TYPES } from '../../../api/types/enum/entityTypes';
import {
	CUSTOM_PROPERTY_ERROR_MESSAGES,
	CUSTOM_PROPERTY_TYPE_OPTIONS,
	CUSTOM_PROPERTY_VISIBILITY_ENTITY_TYPES,
} from '../constants';
import type { CustomPropertyFormType, VisibilityCheckboxProps } from '../types';

const useStyles = createStyles((theme) => ({
	label: {
		fontSize: theme.fontSizes.xs,
		fontWeight: 600,
		color: theme.other.getColor('text/secondary/default'),
	},
}));

const VisibilityCheckbox = memo(
	({
		label,
		entityType,
		visibility,
		setVisibility,
		filteredEntityTypes,
	}: VisibilityCheckboxProps & { filteredEntityTypes: EntityType[] }) => {
		const isAllSelected = visibility.includes(EntityType.all);

		return (
			<Checkbox
				label={label}
				checked={isAllSelected ? true : visibility.includes(entityType)}
				onChange={(event) => {
					if (entityType === EntityType.all) {
						if (event.currentTarget.checked) {
							setVisibility([EntityType.all]);
						} else {
							setVisibility([]);
						}
					} else {
						const visibilityWithoutAll = visibility.filter(
							(v) => v !== EntityType.all
						);
						if (event.currentTarget.checked) {
							setVisibility([...visibilityWithoutAll, entityType]);
						} else {
							if (isAllSelected) {
								const otherTypes = filteredEntityTypes.filter(
									(type) => type !== entityType
								);
								setVisibility(otherTypes);
							} else {
								setVisibility(
									visibilityWithoutAll.filter((v) => v !== entityType)
								);
							}
						}
					}
				}}
			/>
		);
	}
);

export const CustomPropertiesForm = memo(
	({
		fields,
		onCancel,
		onConfirm,
		existingLabels,
		originalLabel,
		isIntegration = false,
		formState,
		setFormState,
		errors,
		setErrors,
		questionsEnabled,
		isPending,
	}: CustomPropertyFormType) => {
		const theme = useMantineTheme();
		const { classes } = useStyles();

		const { catalogEntityTypes, nonCatalogEntityTypes, filteredEntityTypes } =
			useMemo(() => {
				const entityTypes = CUSTOM_PROPERTY_VISIBILITY_ENTITY_TYPES.filter(
					(entityType) => entityType !== EntityType.question || questionsEnabled
				);

				const { catalog, nonCatalog } = entityTypes.reduce(
					(
						acc,
						entityType
					): {
						catalog: Array<{ entityType: EntityType; label: string }>;
						nonCatalog: Array<{ entityType: EntityType; label: string }>;
					} => {
						const displayInfo = getEntityTypeDisplayInfo(entityType);
						const item = {
							entityType,
							label: displayInfo.label,
						};

						if (CATALOG_ENTITY_TYPES.includes(entityType)) {
							acc.catalog.push(item);
						} else {
							acc.nonCatalog.push(item);
						}

						return acc;
					},
					{
						catalog: [] as Array<{ entityType: EntityType; label: string }>,
						nonCatalog: [] as Array<{ entityType: EntityType; label: string }>,
					}
				);

				return {
					catalogEntityTypes: catalog,
					nonCatalogEntityTypes: nonCatalog,
					filteredEntityTypes: entityTypes,
				};
			}, [questionsEnabled]);

		const allCatalogTypesSelected = useMemo(() => {
			if (formState.visibility.includes(EntityType.all)) {
				return true;
			}
			const catalogTypeValues = catalogEntityTypes.map(
				(item) => item.entityType
			);
			return (
				catalogTypeValues.length > 0 &&
				catalogTypeValues.every((entityType) =>
					formState.visibility.includes(entityType)
				)
			);
		}, [formState.visibility, catalogEntityTypes]);

		const handleCatalogGroupToggle = useCallback(
			(checked: boolean) => {
				const expandedVisibility = formState.visibility.includes(EntityType.all)
					? filteredEntityTypes
					: formState.visibility;

				const catalogTypeValues = catalogEntityTypes.map(
					(item) => item.entityType
				);

				let newVisibility: EntityType[];

				if (checked) {
					const nonCatalogSelected = expandedVisibility.filter(
						(v) => !catalogTypeValues.includes(v)
					);

					newVisibility = [...nonCatalogSelected, ...catalogTypeValues];
				} else {
					newVisibility = expandedVisibility.filter(
						(v) => !catalogTypeValues.includes(v)
					);
				}

				if (
					newVisibility.length === filteredEntityTypes.length &&
					filteredEntityTypes.every((type) => newVisibility.includes(type))
				) {
					newVisibility = [EntityType.all];
				}

				setFormState((prev) => ({
					...prev,
					visibility: newVisibility,
				}));
			},
			[
				catalogEntityTypes,
				filteredEntityTypes,
				formState.visibility,
				setFormState,
			]
		);

		const handleVisibilityChange = useCallback(
			(newVisibility: EntityType[]) => {
				setFormState((prev) => ({
					...prev,
					visibility: newVisibility,
				}));
			},
			[setFormState]
		);

		const handleConfirm = useCallback(() => {
			const newErrors: Record<string, string> = {};

			if (
				existingLabels.includes(formState.label) &&
				formState.label !== originalLabel
			) {
				newErrors.label = CUSTOM_PROPERTY_ERROR_MESSAGES.LABEL_ALREADY_EXISTS;
			} else if (fields.includes('label') && isEmpty(formState.label)) {
				newErrors.label = CUSTOM_PROPERTY_ERROR_MESSAGES.LABEL_REQUIRED;
			} else if (
				fields.includes('label') &&
				!/^[a-zA-Z0-9_-]+$/.test(formState.label)
			) {
				newErrors.label = CUSTOM_PROPERTY_ERROR_MESSAGES.LABEL_INVALID;
			} else if (fields.includes('type') && isEmpty(formState.type)) {
				newErrors.type = CUSTOM_PROPERTY_ERROR_MESSAGES.TYPE_REQUIRED;
			}

			if (Object.keys(newErrors).length === 0) {
				onConfirm(formState.label, formState.type, formState.visibility);
			} else {
				setErrors(newErrors);
			}
		}, [
			existingLabels,
			formState.label,
			originalLabel,
			fields,
			formState.type,
			formState.visibility,
			onConfirm,
			setErrors,
		]);

		const isReadOnlyTypeSelector = !!originalLabel;

		return (
			<Stack>
				{fields.includes('label') && (
					<TextInput
						label="Label"
						value={formState.label}
						disabled={isIntegration}
						onChange={(event) => {
							if (isIntegration) {
								return;
							}
							setFormState((prev) => ({
								...prev,
								label: event.currentTarget.value,
							}));
							if (formState.errors.label) {
								setErrors({ ...formState.errors, label: '' });
							}
						}}
						error={formState.errors.label}
					/>
				)}
				{fields.includes('type') && (
					<Select
						label="Type"
						placeholder="Select"
						readOnly={isReadOnlyTypeSelector}
						data={CUSTOM_PROPERTY_TYPE_OPTIONS}
						value={formState.type}
						onChange={(value) => {
							setFormState((prev) => ({ ...prev, type: value || '' }));
							if (errors.type) {
								setErrors({ ...formState.errors, type: '' });
							}
						}}
						error={errors.type}
					/>
				)}
				{fields.includes('visibility') && (
					<Stack spacing="sm">
						<Text size="sm" weight="semibold">
							Resource Type Visibility
						</Text>

						<Stack spacing="xs">
							<VisibilityCheckbox
								label="All"
								entityType={EntityType.all}
								visibility={formState.visibility}
								setVisibility={handleVisibilityChange}
								filteredEntityTypes={filteredEntityTypes}
							/>
							<Stack pl="md" spacing="sm" mb="sm">
								{catalogEntityTypes.length > 0 && (
									<>
										<Checkbox
											label="Catalog"
											checked={allCatalogTypesSelected}
											onChange={(event) =>
												handleCatalogGroupToggle(event.currentTarget.checked)
											}
											classNames={{ label: classes.label }}
										/>
										<Stack spacing="sm" pl="md">
											{catalogEntityTypes.map(
												({ entityType, label: entityLabel }) => (
													<VisibilityCheckbox
														key={entityType}
														label={entityLabel}
														entityType={entityType}
														visibility={formState.visibility}
														setVisibility={handleVisibilityChange}
														filteredEntityTypes={filteredEntityTypes}
													/>
												)
											)}
										</Stack>
									</>
								)}
							</Stack>
							<Stack pl="md" spacing="sm">
								{nonCatalogEntityTypes.length > 0 && (
									<>
										{nonCatalogEntityTypes.map(
											({ entityType, label: entityLabel }) => (
												<VisibilityCheckbox
													key={entityType}
													label={entityLabel}
													entityType={entityType}
													visibility={formState.visibility}
													setVisibility={handleVisibilityChange}
													filteredEntityTypes={filteredEntityTypes}
												/>
											)
										)}
									</>
								)}
							</Stack>
						</Stack>
					</Stack>
				)}

				<Divider
					mx={`-${theme.spacing.md}`}
					px={`${theme.spacing.md}`}
					mt="sm"
				/>
				<Group position="right">
					<Button variant="default" onClick={onCancel}>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={handleConfirm}
						disabled={isPending}
					>
						Save
					</Button>
				</Group>
			</Stack>
		);
	}
);
